import * as d3 from 'd3-color'
import {
  EventType,
  EventTicket,
  GetEventByIdForEditorQuery,
  Statistic,
  StatisticDonation,
  TicketType,
} from '@graphql/__generated/graphql'
import type { Maybe } from '@graphql/__generated/graphql'
import type { Moment } from 'moment'
import { DropdownOptions } from '@components/inputs/DropdownInput'
import { full_timezones, timezones } from '@libs/timezones'

import type { Event } from '@graphql/__generated/graphql'
import { isValidUrl } from './parsers'
import moment from 'moment'
import { PASSWORD_PATTERN } from './validation'
import { BasicOptions } from '@components/inputs/SearchInput'
import { MetricsData, OrderMappedAttendee } from '@type/Hooks/useMetrics.type'

type ValidatePasswordFunction = (password: string) => boolean

export const parseTime = (time: string, tzOffset: number) => {
  return `${time.split('Z').length > 1 ? time.split('Z')[0] : time.slice(0, -6)}${tzOffset < 0 ? '-' : '+'}${
    Math.abs(tzOffset) < 10 ? `0${Math.abs(tzOffset)}` : tzOffset
  }:00`
}

// Accepts only Forumm event objects
export const isEventAfter = (e1: Event, e2: Event) => {
  try {
    if (!e1 || !e2) return 0
    const e1Start = moment(e1.event?.startDateTime)
    const e2Start = moment(e2.event?.startDateTime)
    if (e1Start.isBefore(e2Start)) return -1
    return 1
  } catch (_ex) {
    console.error(_ex)
    return 0
  }
}

export const getTimezones = (selectedTzString?: string): DropdownOptions[] => {
  const payload = full_timezones
    .sort((a, b) => {
      const clientOffset = Math.abs(getClientTimezoneOffset())
      const aRelativeOffset = Math.abs(clientOffset - Math.abs(a.offset))
      const bRelativeOffset = Math.abs(clientOffset - Math.abs(b.offset))
      return aRelativeOffset - bRelativeOffset
    })
    .map((tz) => {
      return {
        label: tz.text,
        value: `${tz.offset}::${tz.abbr}`,
      }
    })
    .map((tz) => {
      if (tz.value === selectedTzString) return { ...tz, selected: true }
      return tz
    })

  return payload
}

export const getClientTimezoneUTCId = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export const getClientTimezoneOffset = (): number => {
  return Math.floor(new Date().getTimezoneOffset() / 60) * -1
}

export const getClientTimezoneString = (): string => {
  const clientTz = getClientTimezoneOffset()
  const utcId = getClientTimezoneUTCId()
  const targetTz = full_timezones.find(
    (tz) => tz.offset === clientTz && tz.utc.includes(utcId)
  )
  return `${targetTz?.offset}::${targetTz?.abbr}`
}

type EventTimeContext = {
  eventType?: EventType | string | null
  timeZone?: string | null
}

const getLastSundayAt1Utc = (year: number, month: number) => {
  const date = new Date(Date.UTC(year, month + 1, 0, 1))
  date.setUTCDate(date.getUTCDate() - date.getUTCDay())
  return date
}

const getUkOffsetMinutes = (dateTime: string) => {
  const date = new Date(dateTime)
  if (Number.isNaN(date.getTime())) return undefined

  const year = date.getUTCFullYear()
  return date >= getLastSundayAt1Utc(year, 2) &&
    date < getLastSundayAt1Utc(year, 9)
    ? 60
    : 0
}

export const getEventTimezoneOffsetMinutes = (
  timeZone?: Maybe<string>,
  dateTime?: Maybe<string>
) => {
  if (!timeZone) return undefined

  const [offsetString, abbr] = timeZone.split('::')
  const offset = Number(offsetString)

  if (Number.isNaN(offset)) return undefined
  if (!dateTime) return offset * 60

  const ukOffsetMinutes =
    offset === 1 && abbr === 'BST' ? getUkOffsetMinutes(dateTime) : undefined
  if (ukOffsetMinutes !== undefined) return ukOffsetMinutes

  const utcId = full_timezones.find(
    (tz) => tz.offset === offset && tz.abbr === abbr
  )?.utc?.[0]

  if (!utcId) return offset * 60

  try {
    const timeZoneName = new Intl.DateTimeFormat('en-US', {
      timeZone: utcId,
      timeZoneName: 'shortOffset',
    })
      .formatToParts(new Date(dateTime))
      .find((part) => part.type === 'timeZoneName')?.value
    const match = timeZoneName?.match(/^GMT(?:(-|\+)(\d{1,2})(?::(\d{2}))?)?$/)

    if (!match) return offset * 60

    return (match[1] === '-' ? -1 : 1) *
      ((Number(match[2]) || 0) * 60 + (Number(match[3]) || 0))
  } catch (_ex) {
    return offset * 60
  }
}

const isInPersonEvent = (eventType?: EventTimeContext['eventType']) =>
  eventType === EventType.InPerson || eventType === 'IN_PERSON'

export const getEventDateTimeMoment = (
  dateTime?: Maybe<string>,
  event?: EventTimeContext | null
) => {
  if (!dateTime) return moment(dateTime)
  if (!isInPersonEvent(event?.eventType)) return moment(dateTime)

  const eventTimezoneOffsetMinutes = getEventTimezoneOffsetMinutes(
    event?.timeZone,
    dateTime
  )
  if (eventTimezoneOffsetMinutes !== undefined) {
    return moment(dateTime).utcOffset(eventTimezoneOffsetMinutes)
  }

  return moment.parseZone(dateTime)
}

export const formatEventDateTime = (
  dateTime: Maybe<string> | undefined,
  format: string,
  event?: EventTimeContext | null
) => getEventDateTimeMoment(dateTime, event).format(format)

export const isBasicOptions = (
  options: BasicOptions[] | DropdownOptions[]
): options is BasicOptions[] => {
  if (options.length === 0) {
    return true
  }
  const first = options[0]
  return 'id' in first && !('value' in first)
}

export const isObject = (obj: any) => typeof obj === 'object'

export const isSame = (a: any, b: any) => {
  try {
    if (isObject(a) && isObject(b)) {
      const stringifiedA = JSON.stringify(a)
      const stringifiedB = JSON.stringify(b)
      return stringifiedA.includes(stringifiedB)
    }
    return a == b
  } catch (err) {
    console.error(err)
    return false
  }
}

export const cleanGqlPayload = <T>(data: T): T | null => {
  try {
    return JSON.parse(
      JSON.stringify(data, (name, val) => {
        if (name === '__typename') {
          delete val[name]
        } else {
          return val
        }
      })
    )
  } catch (err) {
    console.error(err)
    return null
  }
}

export const cleanPayload = <T>(data: T, keyToRemove: keyof T): T | null => {
  try {
    return JSON.parse(
      JSON.stringify(data, (name, val) => {
        if (name === keyToRemove) {
          delete val[name]
        } else {
          return val
        }
      })
    )
  } catch (err) {
    console.error(err)
    return null
  }
}

export const cleanEventPayload = (data: GetEventByIdForEditorQuery) => {
  return cleanGqlPayload(data.getEventById)
}

export const debounce = (cb: any, delay = 1000) => {
  let timeout: NodeJS.Timeout | undefined
  return (...args: any[]) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => {
      cb(...args)
    }, delay)
  }
}

export const flatten = <T>(data: T, parentKey?: string): Record<string, T> => {
  let output = {}
  for (let key in data) {
    let val = data[key]
    let newKey = parentKey ? `${parentKey}_${key}` : key
    if (val && typeof val === 'object') {
      const flat = flatten(val, newKey)
      output = { ...output, ...flat }
    } else {
      output = { ...output, [newKey]: val }
    }
  }
  return output
}

export const toArray = (data: any | any[]) => {
  if (!data) return []
  if (Array.isArray(data)) {
    if (Array.isArray(data[0]) && data.length < 2) return data[0]
    return data
  }
  return [data]
}

export const truncateString = (string?: string | null, length: number = 8) => {
  if (!string) return ''
  return `${string.slice(0, length)}${string.length > length ? '...' : ''}`
}

export const roundAmount = (amount: number | string): string => {
  try {
    const parsedAmount = parseFloat(amount.toString())
    if (isNaN(parsedAmount)) {
      return `${amount}`
    }
    return parsedAmount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  } catch (_ex) {
    return `${amount}`
  }
}

export const isJSON = (text?: string) => {
  if (!text) return false
  if (typeof text !== 'string') return false
  try {
    JSON.parse(text)
    return true
  } catch (_) {
    return false
  }
}

export const generateSecurePassword = () => {
  const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz'
  const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const numberChars = '0123456789'
  const specialChars = '-'
  const randomLowercase =
    lowercaseChars[Math.floor(Math.random() * lowercaseChars.length)]
  const randomUppercase =
    uppercaseChars[Math.floor(Math.random() * uppercaseChars.length)]
  const randomNumber =
    numberChars[Math.floor(Math.random() * numberChars.length)]
  const randomSpecial =
    specialChars[Math.floor(Math.random() * specialChars.length)]
  const allChars = lowercaseChars + uppercaseChars + numberChars + specialChars
  let password =
    randomLowercase + randomUppercase + randomNumber + randomSpecial
  for (let i = 4; i < 12; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)]
  }
  return password
    .split('')
    .sort(() => 0.5 - Math.random())
    .join('')
}

export const validateCognitoPassword: ValidatePasswordFunction = (password) => {
  // const lowercaseLetters = 'a-z'
  // const uppercaseLetters = 'A-Z'
  // const numbers = '0-9'
  // const specialChars =
  //   '\\^\\$\\*\\.\\[\\]\\{\\}\\(\\)\\?\\"\\!\\@\\#\\%\\&\\/\\\\,\\>\\<\\\'\\:\\;\\|\\_\\~\\`\\=\\+\\-'
  //
  // const passwordPattern = `^
  //   (?=(.*[${lowercaseLetters}]){1,})
  //   (?=(.*[${uppercaseLetters}]){1,})
  //   (?=(.*[${numbers}]){1,})
  //   (?=(.*[${specialChars}]{1,}))
  //   .{8,256}
  // $`.replace(/\s+/g, '') // Remove whitespace from the multiline string

  const regex = new RegExp(PASSWORD_PATTERN)
  return regex.test(password)
}

export const darkenColor = (color: string, percentage: number) => {
  let baseColor = d3.hsl(color)
  baseColor.l = Math.max(0, baseColor.l * (1 - percentage))
  return baseColor.formatHex()
}

export const lightenColor = (color: string, percentage: number) => {
  let baseColor = d3.hsl(color)
  baseColor.l = Math.max(0, baseColor.l * (1 + percentage))
  return baseColor.formatHex()
}

export const getDynamicStyle = (colorBase?: string) => {
  let dynamicStyle = {
    '--color-change-start': '#3763E9',
    '--color-change-middle': '#6FC7F0',
    '--color-change-end': '#7BECCD',
  }
  if (!colorBase) return dynamicStyle
  dynamicStyle = {
    '--color-change-start': darkenColor(colorBase, 0.4),
    '--color-change-middle': colorBase,
    '--color-change-end': lightenColor(colorBase, 0.4),
  }
  return dynamicStyle
}

export const getContrastColor = (colorInput?: string) => {
  const defaultColor = '#000000' // Default color if none is provided
  const colorObject = d3.color(colorInput || defaultColor)

  if (!colorObject) {
    return 'black' // Fallback color
  }

  // Extract RGB values
  const rgb = colorObject.rgb()
  const r = rgb.r / 255
  const g = rgb.g / 255
  const b = rgb.b / 255

  // Calculate luminance
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b

  // Decide on the contrast color based on luminance
  return luminance > 0.5 ? 'black' : 'white'
}

export const openInNewTab = (url: string) => {
  if (!url) return

  if (isValidUrl(url)) {
    url.replace('http', 'https')
    if (!url.includes('https')) url = `https://${url}`
  }

  if (window) window.open(url, '_blank', '')?.focus()
}

export const openInSameTab = (url: string) => {
  if (!url) return

  if (isValidUrl(url)) {
    url.replace('http', 'https')
    if (!url.includes('https')) url = `https://${url}`
  }

  if (window) window.open(url, '_self')?.focus()
}

export const isBusinessEmail = (email: string) => {
  const commonProviders = [
    'gmail.com',
    'outlook.com',
    'yahoo.com',
    'hotmail.com',
    'aol.com',
    'icloud.com',
  ]
  const emailDomain = email.split('@')[1]
  return !commonProviders.includes(emailDomain.toLowerCase())
}

//Unique/Total users in a donation campaign (does not mean they donated)
export const getUniqueUsersDonation = (
  metrics: Statistic[],
  input: string
): string[] => {
  const uniqueUsers = new Set<string>()
  metrics
    .filter((metric) => metric.url && metric.url.includes('donation'))
    .forEach((metric) => {
      const userIdentifier = metric.userId || metric.anonymousId
      if (userIdentifier) {
        uniqueUsers.add(userIdentifier)
      }
    })
  return Array.from(uniqueUsers)
}

export const getTotalUsersDonation = (
  metrics: Statistic[],
  input: string
): {
  datetime: string
  userId: string
  url: Maybe<string>
  donation: Maybe<StatisticDonation>
}[] => {
  return metrics
    .filter(
      (metric) =>
        metric.datetime &&
        metric.userId &&
        metric.url &&
        metric.url.includes('donation')
    )
    .map((metric) => {
      const userIdentifier = metric.userId
      return {
        datetime: metric.datetime as string,
        userId: (userIdentifier as string) ?? null,
        url: metric.url ?? null,
        donation: metric.donation ?? null,
      }
    })
}

export type OptType = {
  id: string
  title: string
  donationUrl: string
}
export type TicketInfo = {
  eventName: string
  ticketType: string
  ticketsSold: number
  ticketPrice: number
  totalRevenue: number
}

export const getTicketInfo = (
  events: Event[],
  attendees: OrderMappedAttendee[]
): TicketInfo[] => {
  const payload = events.flatMap((event) => {
    const ticketCounts: { [key: string]: number } = {}
    const ticketPrices: { [key: string]: number } = {}

    const eventsToFix = ['31fffe01-557f-4c3c-a7f3-cb1ec9772277']

    const legacyTicketFix = (): EventTicket[] => {
      const payload: EventTicket[] = []
      if (eventsToFix.includes(event.eventId)) {
        if (event.eventId === '31fffe01-557f-4c3c-a7f3-cb1ec9772277')
          payload.push({
            title: 'STUDENT',
            ticketType: TicketType.Paid,
            price: 10,
            quantity: 200,
          } as EventTicket)
      }

      return payload
    }

    const tickets = [...(event?.tickets ?? []), ...legacyTicketFix()]

    event.attendees?.forEach((attendee) => {
      if (attendee.ticketTitle) {
        ticketCounts[attendee.ticketTitle.trim().toUpperCase()] =
          (ticketCounts[attendee.ticketTitle.trim().toUpperCase()] || 0) + 1
      }
    })

    tickets.forEach((ticket) => {
      if (ticket.title && ticket.price !== undefined) {
        ticketPrices[ticket.title.trim().toUpperCase()] = Number(ticket.price)
      }
    })

    return Object.entries(ticketCounts).map(([ticketType, ticketsSold]) => {
      const ticketPrice = ticketPrices[ticketType] || 0
      // const totalRevenue = ticketsSold * ticketPrice
      const totalRevenue = attendees
        ?.filter(
          (f) =>
            f.ticketTitle?.trim().toLowerCase() ===
              ticketType.trim().toLowerCase() && f.eventId === event.eventId
        )
        .reduce((total, order) => (total += order.pricePaid), 0)

      const payload = {
        eventName: event.event?.title || '',
        ticketType,
        ticketsSold,
        ticketPrice,
        totalRevenue,
      }
      return payload
    })
  })
  return payload
}

export const getSessionReplay = (event: Partial<Event>): false | string => {
  // console.log(`[SessionReplay]: `, { event })
  if (!event) return false

  const orgName = event.event?.organizationName?.toLowerCase() ?? ''
  // console.log({ orgName, eventId: event.eventId })
  if (orgName === 'forumm') {
    // Beyond the Feed: Building a Strategic Social Media Culture
    if (event.eventId === '4b3f843b-3fef-4943-9df1-8e5a52d71ce5')
      return 'https://youtu.be/pcurK5tY3wo'

    if (event.eventId === '3eec0348-344d-47ce-ab39-98d84b93c933')
      return 'https://www.youtube.com/watch?v=PSjwhLREFKo'
  }

  //Prof Anton
  if (event.eventId === 'e172670e-a062-4f05-9e33-b334a57fff93')
    return 'https://www.youtube.com/watch?v=RF_kgb52i7c'

  if (event.eventId === 'f8dc01c6-417b-4bb0-8152-fba7f33eddfa')
    return 'https://www.youtube.com/watch?v=TXPfo7a_s2g'

  if (orgName === 'university of glasgow') {
    // Global Burns 2025: Burns and the Environment
    if (event.eventId === 'e4af5751-be4f-4a28-a17c-4e6fe64adf25')
      return 'https://youtu.be/vXcCKVKNYlg?si=hL-xdBZ4Ltoaq2iO'
  }
  // NOTE: SPS Session 1: We wish you a Merry Menomas
  if (event.eventId === '0ac01ca8-6756-464d-b272-474be8a1358c')
    return 'https://forumm-images-prod.s3.eu-west-1.amazonaws.com/user-content/manual-upload/SPS+Session+01+-+Platform+Recording.mp4'

  return false
}
